import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, AlertTriangle, FileText, Car } from 'lucide-react';
import { Vehicle } from '../../../types/saas';
import saasApiService from '../../../services/saasApi';
import ExpenseForm from '../Finance/ExpenseForm';

interface VehicleListProps {
  onEdit: (vehicle: Vehicle) => void;
  onAdd: () => void;
  refreshTrigger: number;
}

const VehicleList: React.FC<VehicleListProps> = ({ onEdit, onAdd, refreshTrigger }) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const PREF_KEY = 'vehicleList.prefs';
  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});
  const STORAGE_KEY = 'vehicleList.expandedIds';
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseDraft, setExpenseDraft] = useState<any | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchVehicles();
    }, 100);
    return () => clearTimeout(timer);
  }, [refreshTrigger]);

  // Load expanded state from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setExpandedIds(parsed);
        }
      }

      const prefsRaw = localStorage.getItem(PREF_KEY);
      if (prefsRaw) {
        const prefs = JSON.parse(prefsRaw);
        if (prefs) {
          if (typeof prefs.searchTerm === 'string') setSearchTerm(prefs.searchTerm);
          if (typeof prefs.statusFilter === 'string') setStatusFilter(prefs.statusFilter);
        }
      }
    } catch (e) {
      console.warn('No se pudo leer expandedIds de localStorage');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist expanded state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedIds));
    } catch (e) {
      // ignore
    }
  }, [expandedIds]);

  // Persist search and filter
  useEffect(() => {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({ searchTerm, statusFilter }));
    } catch (e) {
      // ignore
    }
  }, [searchTerm, statusFilter]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      console.log('Fetching vehicles...');
      const response = await saasApiService.getVehicles();
      console.log('Vehicles response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', response ? Object.keys(response) : 'null');
      
      // El backend puede devolver diferentes estructuras
      let vehicleData = [];
      if (response && response.vehicles && Array.isArray(response.vehicles)) {
        // Estructura directa: { vehicles: [...], pagination: {...} }
        vehicleData = response.vehicles;
        console.log('Using response.vehicles:', vehicleData.length, 'vehicles');
      } else if (response && response.data && response.data.vehicles) {
        // Estructura anidada: { success: true, data: { vehicles: [...], pagination: {...} } }
        vehicleData = response.data.vehicles;
        console.log('Using response.data.vehicles:', vehicleData.length, 'vehicles');
      } else if (Array.isArray(response)) {
        vehicleData = response;
        console.log('Using response as array:', vehicleData.length, 'vehicles');
      } else if (response && response.items) {
        vehicleData = response.items;
        console.log('Using response.items:', vehicleData.length, 'vehicles');
      } else if (response && response.data && Array.isArray(response.data)) {
        vehicleData = response.data;
        console.log('Using response.data as array:', vehicleData.length, 'vehicles');
      } else {
        console.log('No vehicle data found in response structure');
      }
      
      console.log('Final vehicleData:', vehicleData);
      console.log('Setting vehicles count:', vehicleData.length);
      setVehicles(vehicleData);
    } catch (error: any) {
      console.error('Error fetching vehicles:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const maintDefs: { key: string; label: string; lastKey: keyof Vehicle; intervalKey: keyof Vehicle }[] = [
    { key: 'engine_oil', label: 'Aceite Motor', lastKey: 'maint_engine_oil_last_odo' as any, intervalKey: 'maint_engine_oil_interval_km' as any },
    { key: 'filters', label: 'Filtros', lastKey: 'maint_filters_last_odo' as any, intervalKey: 'maint_filters_interval_km' as any },
    { key: 'gearbox_oil', label: 'Aceite de Caja', lastKey: 'maint_gearbox_oil_last_odo' as any, intervalKey: 'maint_gearbox_oil_interval_km' as any },
    { key: 'crown_oil', label: 'Aceite de Corona', lastKey: 'maint_crown_oil_last_odo' as any, intervalKey: 'maint_crown_oil_interval_km' as any },
    { key: 'valve_calibration', label: 'Calibración Válvulas', lastKey: 'maint_valve_calib_last_odo' as any, intervalKey: 'maint_valve_calib_interval_km' as any }
  ];

  const calcMaint = (vehicle: any, def: any) => {
    const last = parseFloat(vehicle[def.lastKey] || 0);
    const interval = parseInt(vehicle[def.intervalKey] || 0, 10);
    const curr = parseFloat(vehicle.mileage || 0);
    const traveled = last ? Math.max(0, curr - last) : 0;
    const remaining = interval ? Math.max(0, interval - traveled) : 0;
    const ratio = interval ? remaining / interval : 1;
    let color = 'bg-green-100 text-green-800 border-green-300';
    if (ratio <= 0.25) color = 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (remaining === 0 && traveled > 0) color = 'bg-red-100 text-red-800 border-red-300';
    return { last, interval, curr, traveled, remaining, color };
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este vehículo?')) {
      try {
        await saasApiService.deleteVehicle(id);
        fetchVehicles();
      } catch (error) {
        console.error('Error deleting vehicle:', error);
      }
    }
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = 
      vehicle.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.license_plate.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || vehicle.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const total = filteredVehicles.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const pageVehicles = filteredVehicles.slice(startIdx, endIdx);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'maintenance': return 'Mantenimiento';
      case 'inactive': return 'Inactivo';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Vehículos</h1>
          <p className="text-gray-600">Administra tu flota de vehículos</p>
        </div>
        <button
          onClick={onAdd}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Vehículo
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Buscar por marca, modelo o placa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="maintenance">Mantenimiento</option>
          <option value="inactive">Inactivo</option>
        </select>
      </div>

      {/* Vehicle Grid */}
      {filteredVehicles.length === 0 ? (
        <div className="text-center py-12">
          <Car className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay vehículos</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' 
              ? 'No se encontraron vehículos con los filtros aplicados.'
              : 'Comienza agregando tu primer vehículo.'
            }
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <div className="mt-6">
              <button
                onClick={onAdd}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar Vehículo
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pageVehicles.map((vehicle) => (
            <div key={vehicle.id} className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div
                  className="flex justify-between items-start mb-4 cursor-pointer select-none"
                  onClick={() => setExpandedIds((prev) => ({ ...prev, [vehicle.id]: !prev[vehicle.id] }))}
                  title="Ver/ocultar detalles del vehículo"
                >
                  <div className="flex items-center">
                    <Car className="h-8 w-8 text-blue-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {vehicle.brand} {vehicle.model}
                      </h3>
                      <p className="text-sm text-gray-500">{vehicle.year}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vehicle.status)}`}>
                    {getStatusText(vehicle.status)}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Placa:</span>
                    <span className="text-sm font-medium text-gray-900">{vehicle.license_plate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Kilometraje:</span>
                    <span className="text-sm font-medium text-gray-900">{vehicle.mileage.toLocaleString()} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Combustible:</span>
                    <span className="text-sm font-medium text-gray-900 capitalize">{vehicle.fuel_type}</span>
                  </div>
                  {vehicle.capacity_tons && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Capacidad:</span>
                      <span className="text-sm font-medium text-gray-900">{vehicle.capacity_tons} ton</span>
                    </div>
                  )}
                </div>

                {/* Document alerts */}
                {(vehicle.registration_expiry || vehicle.insurance_expiry) && (
                  <div className="mb-4">
                    {vehicle.registration_expiry && new Date(vehicle.registration_expiry) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                      <div className="flex items-center text-yellow-600 text-xs mb-1">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Registro vence pronto
                      </div>
                    )}
                    {vehicle.insurance_expiry && new Date(vehicle.insurance_expiry) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                      <div className="flex items-center text-yellow-600 text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Seguro vence pronto
                      </div>
                    )}
                  </div>
                )}

                {/* Maintenance section (collapsible) */}
                {expandedIds[vehicle.id] && (
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Mantenimientos</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {maintDefs.map((def) => {
                        const m = calcMaint(vehicle as any, def);
                        return (
                          <div key={def.key} className={`border rounded-md p-2 text-xs ${m.color}`}>
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{def.label}</span>
                              <button
                                className="ml-2 px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpenseDraft({
                                    vehicle_id: vehicle.id,
                                    expense_type: 'maintenance',
                                    maintenance_category: def.key,
                                    expense_date: new Date().toISOString().split('T')[0]
                                  });
                                  setShowExpenseForm(true);
                                }}
                              >
                                Registrar
                              </button>
                            </div>
                            <div className="mt-1">Último: {m.last ? `${m.last.toLocaleString()} km` : '—'}</div>
                            <div>Desde último: {m.traveled.toLocaleString()} km</div>
                            <div>Faltan: {m.remaining.toLocaleString()} km</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div className="flex space-x-2">
                    {vehicle.registration_document && (
                      <div title="Documento de registro">
                        <FileText className="h-4 w-4 text-green-600" />
                      </div>
                    )}
                    {vehicle.insurance_document && (
                      <div title="Documento de seguro">
                        <FileText className="h-4 w-4 text-blue-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onEdit(vehicle)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Editar vehículo"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(vehicle.id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Eliminar vehículo"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Pagination controls */}
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-600">
            Mostrando {total === 0 ? 0 : startIdx + 1} - {Math.min(endIdx, total)} de {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-50"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              Anterior
            </button>
            <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
            <button
              className="px-3 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-50"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Siguiente
            </button>
            <select
              className="ml-2 px-2 py-1.5 text-sm border border-gray-300 rounded"
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
            >
              <option value={6}>6</option>
              <option value={9}>9</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
            </select>
          </div>
        </div>
        </>
      )}

      {/* Expense Form Modal prefilled from vehicle */}
      <ExpenseForm
        isOpen={showExpenseForm}
        onClose={() => {
          setShowExpenseForm(false);
          setExpenseDraft(null);
        }}
        onSave={() => {
          setShowExpenseForm(false);
          setExpenseDraft(null);
          fetchVehicles();
        }}
        expense={expenseDraft}
      />
    </div>
  );
};

export default VehicleList;
